/**
 * Bookings Management Page
 * View, filter, and manage all bookings
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Calendar, ChevronDown, ChevronLeft, ChevronRight, Trash2, X } from 'lucide-react';
import { Listbox } from '@headlessui/react';
import { adminAPI } from '../api/adminEndpoints';
import { realAPI } from '../api/realEndpoints';
import { formatDate, formatCurrency } from '../utils/helpers';
import { getUserImageSrc } from '../utils/userImage';
import { useToast } from '../context/ToastContext';
import type { BookingFilters } from '../api/adminTypes';
import Card from '../components/Card';
import Button from '../components/Button';
import Modal from '../components/Modal';
import './ContentManagement.css';

// Extended Booking type for API response
interface APIBooking {
  id: number;
  userId: number;
  providerId: number;
  categoryId: number;
  serviceId: number;
  bookingDate: string;
  type?: string;
  amount?: number | string | null;
  serviceName?: string | null;
  status: string;
  description: string | null;
  createdAt: string;
  completeAt: string | null;
  bookingAddressInfo: any;
  customerInfo: {
    id: number;
    name: string;
    role: string;
    image: string | null;
    averageRating: number;
    totalRating: number;
    signInProvider?: string[] | null;
  };
  providerInfo: {
    id: number;
    name: string;
    role: string;
    image: string;
    averageRating: number;
    totalRating: number;
    signInProvider?: string[] | null;
  };
  categoryInfo: {
    id: number;
    name: string;
    description: string;
    image: string;
    parentId: number | null;
    status: string;
    createdAt: string;
    parentCategory: any;
  };
  servicesInfo?: {
    id: number;
    providerId: number;
    serviceId: number;
    serviceName: string;
    description: string;
    price: string;
    serviceImage?:
      | string
      | string[]
      | {
          id?: number | string;
          url: string;
        }[];
    createdAt: string;
  } | null;
  ratingInfo?: {
    rating: number;
    review?: string;
    createdAt: string;
  };
}

export default function Bookings() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { success, error } = useToast();
  const [bookings, setBookings] = useState<APIBooking[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState<string | number | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<APIBooking | null>(null);

  const [sortField, setSortField] = useState<
    | 'bookingDate'
    | 'status'
    | 'createdAt'
    | 'providerName'
    | 'customerName'
    | 'serviceName'
    | 'price'
    | null
  >(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Date picker states
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);

  // Filters
  const [filters, setFilters] = useState<BookingFilters>({
    dateFrom: '',
    dateTo: '',
    status: '',
  });

  // Status filter options
const statusOptions = [
  { label: 'All Status', value: 'all', color: 'text-gray-600' },

  { label: 'Pending', value: 'pending', color: 'text-amber-500' },

  { label: 'Pending Approval', value: 'pending_approval', color: 'text-blue-500' },

  { label: 'Accepted', value: 'accepted', color: 'text-indigo-600' },

  { label: 'Completed', value: 'completed', color: 'text-green-600' },

  { label: 'Cancelled', value: 'cancelled', color: 'text-red-500' },

  { label: 'Rejected', value: 'rejected', color: 'text-rose-600' },
];

  const LIMIT = 10;
  const isFetchingRef = useRef(false);
  const isResettingRef = useRef(false);

  const fetchBookings = useCallback(
    async (statusOverride?: string, dateFromOverride?: string, dateToOverride?: string) => {
      if (isFetchingRef.current) return; // Prevent multiple simultaneous calls

      isFetchingRef.current = true;
      setIsLoading(true);
      try {
        const offset = (page - 1) * LIMIT;
        // Use overrides if provided (checking against undefined to allow empty strings), otherwise use state
        const statusToUse = statusOverride !== undefined ? statusOverride : filters.status;
        const dateFromToUse = dateFromOverride !== undefined ? dateFromOverride : filters.dateFrom;
        const dateToToUse = dateToOverride !== undefined ? dateToOverride : filters.dateTo;

        // Send dates if they are selected (match UsersAnalyticsGraph pattern)
        const apiDateFrom = dateFromToUse || undefined;
        const apiDateTo = dateToToUse || undefined;
        const shortBy = sortField || undefined;
        const shortType = sortOrder || undefined;

        const data = await realAPI.bookings.getBookings(
          LIMIT,
          offset,
          statusToUse,
          apiDateFrom,
          apiDateTo,
          shortBy,
          shortType
        );
        setBookings(data?.results || []);
        setTotal(data?.counts || 0);
      } catch (err) {
        console.error('Failed to fetch bookings:', err);
        setBookings([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    },
    [page, filters.status, filters.dateFrom, filters.dateTo, sortField, sortOrder] // Added sortField and sortOrder to dependencies
  );

  // Handle sort click
  const handleSortClick = (field: string) => {
    const newSortField = field as
      | 'bookingDate'
      | 'status'
      | 'createdAt'
      | 'providerName'
      | 'customerName'
      | 'serviceName'
      | 'price'
      | null;
    const newSortOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';

    setSortField(newSortField);
    setSortOrder(newSortOrder);
    setPage(1); // Reset to first page when changing sort

    // The useEffect will trigger a refetch because sortField and sortOrder are in the dependency array
  };

  // Initial data fetch and URL parameter handling
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const statusParam = searchParams.get('status');
    const fromDashboardCompleted = sessionStorage.getItem('fromDashboardCompleted');

    // Clear the session storage flag immediately
    sessionStorage.removeItem('fromDashboardCompleted');

    if (fromDashboardCompleted === 'true' && statusParam === 'completed') {
      // Skip API call when coming from dashboard completed bookings
      // Just set the filter without fetching
      setFilters((prev) => ({ ...prev, status: 'completed' }));
      setIsLoading(false);
      return;
    }

    // Normal API call for all other cases
    if (statusParam && statusParam !== 'all') {
      setFilters((prev) => ({ ...prev, status: statusParam }));
      fetchBookings(statusParam);
    } else {
      // Clear status filter when no status parameter
      setFilters((prev) => ({ ...prev, status: '' }));
      fetchBookings();
    }
  }, [page, location.search, sortField, sortOrder]); // Remove date filters from deps to prevent duplicate calls

  // Handle date filter changes separately
  useEffect(() => {
    // Only fetch if we have BOTH date filters and no status parameter in URL
    const searchParams = new URLSearchParams(location.search);
    const statusParam = searchParams.get('status');

    // Check if both dates are present
    if (filters.dateFrom && filters.dateTo && !statusParam) {
      setPage(1);
      fetchBookings();
    }
  }, [filters.dateFrom, filters.dateTo]); // Only depend on date filters

  // Handle URL parameter for booking details
  useEffect(() => {
    const fetchBookingDetails = async () => {
      if (!id) {
        // Close modal when no ID in URL
        setIsDetailsModalOpen(false);
        setSelectedBooking(null);
        return;
      }

      try {
        const details = await realAPI.bookings.getBookingDetails(id);
        // Check if data is nested under result
        const bookingData = details.result || details;

        setSelectedBooking(bookingData as APIBooking);
        setIsDetailsModalOpen(true);
      } catch (err) {
        console.error('Failed to fetch booking details:', err);
        setIsDetailsModalOpen(false);
        setSelectedBooking(null);
      }
    };

    fetchBookingDetails();
  }, [id]);

  const handleFilterChange = (key: keyof BookingFilters, value: string) => {
    const newFilters = { ...filters, [key]: value };

    // Determine if we should clear data
    let shouldClear = true;

    // If we are changing a date...
    if (key === 'dateFrom' || key === 'dateTo') {
      // ...check if we have both dates in the NEW state
      const hasBothDates = newFilters.dateFrom && newFilters.dateTo;

      // If we don't have both dates yet, DO NOT clear the data.
      // We want to keep the old list visible until the user finishes selecting the range.
      if (!hasBothDates) {
        shouldClear = false;
      }
    }

    if (shouldClear) {
      setBookings([]);
      setTotal(0);
      setPage(1); // Reset page only when we are actually going to fetch/refresh
    }

    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    // Reset all filter states directly (like Customers/Providers)
    setFilters({
      dateFrom: '',
      dateTo: '',
      status: '',
    });
    setPage(1);
    setShowStartCalendar(false);
    setShowEndCalendar(false);
    setSortField('createdAt');
    setSortOrder('desc');

    // Clear data and navigate
    setBookings([]);
    setTotal(0);
    navigate('/bookings', { replace: true });

    // Fetch all bookings immediately with explicit empty overrides
    fetchBookings('', '', '');
  };

  const handleStatusFilterChange = (value: string) => {
    const newStatus = value === 'all' ? '' : value;
    setFilters((prev) => ({ ...prev, status: newStatus }));
    setPage(1);

    // Clear data immediately to prevent flashing
    setBookings([]);
    setTotal(0);

    // Update URL without page reload
    navigate(`/bookings${newStatus ? `?status=${newStatus}` : ''}`, { replace: true });

    // Trigger API call with new status (or empty string for all)
    fetchBookings(newStatus);
  };

  const handleToDateChange = (date: string) => {
    handleFilterChange('dateTo', date);
  };

  // Custom Date Picker Component (enhanced from UsersAnalyticsGraph)
  const DatePicker = ({
    selectedDate,
    onDateChange,
    onClose,
    isOpen,
    minDate,
  }: {
    selectedDate?: string;
    onDateChange: (date: string) => void;
    onClose: () => void;
    isOpen: boolean;
    minDate?: string;
  }) => {
    // Smart initial month: if there's a selectedDate, use it.
    // If there's a minDate, use that.
    // Otherwise current date.
    // Fix: Parse YYYY-MM-DD explicitly as local components to avoid UTC timezone shifts
    const parseDateLocal = (dateStr: string) => {
      if (!dateStr) return new Date();
      const [y, m, d] = dateStr.split('-').map(Number);
      return new Date(y, m - 1, d);
    };

    const getInitialMonth = () => {
      if (selectedDate) {
        return parseDateLocal(selectedDate);
      }
      if (minDate) {
        return parseDateLocal(minDate);
      }
      return new Date();
    };

    const [currentMonth, setCurrentMonth] = useState(getInitialMonth());

    // Update current month when opening if needed
    useEffect(() => {
      if (isOpen) {
        setCurrentMonth(getInitialMonth());
      }
    }, [isOpen, selectedDate, minDate]);

    // Check if previous button should be disabled
    const isPreviousDisabled = useMemo(() => {
      if (!minDate) return false;
      // Parse minDate as local components to ensure accurate month comparison
      const [minY, minM] = minDate.split('-').map(Number);
      // minM is 1-based (from split), getMonth() is 0-based
      // We disable if current view is equal to (or technically before, though usage prevents that) the minDate month
      return currentMonth.getFullYear() === minY && currentMonth.getMonth() === minM - 1;
    }, [currentMonth, minDate]);

    if (!isOpen) return null;

    const getDaysInMonth = (date: Date) => {
      return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date: Date) => {
      return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    };

    const formatDateForInput = (date: Date) => {
      // Get date in YYYY-MM-DD format without timezone conversion
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const handleDateSelect = (day: number) => {
      const selected = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      onDateChange(formatDateForInput(selected));
      onClose();
    };

    const handleMonthChange = (increment: number) => {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + increment, 1));
    };

    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

    return (
      <>
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />
        {/* Calendar */}
        <div
          className="fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-80"
          style={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => !isPreviousDisabled && handleMonthChange(-1)}
              disabled={isPreviousDisabled}
              className={`p-2 rounded-lg transition-colors ${
                isPreviousDisabled
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleMonthChange(1)}
              className="p-2 rounded-lg transition-colors hover:bg-gray-100"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>

          <div className="text-center mb-4">
            <h3 className="font-semibold text-gray-900">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h3>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map((day) => (
              <div
                key={day}
                className="h-8 flex items-center justify-center text-xs font-medium text-gray-500"
              >
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDay }).map((_, index) => (
              <div key={`empty-${index}`} className="h-10" />
            ))}
            {Array.from({ length: daysInMonth }).map((_, index) => {
              const day = index + 1;
              const currentDate = new Date(
                currentMonth.getFullYear(),
                currentMonth.getMonth(),
                day
              );
              const dateString = formatDateForInput(currentDate);
              const isSelected = selectedDate && dateString === selectedDate;
              const isToday = dateString === formatDateForInput(new Date());

              // Check if date is disabled (before minDate)
              let isDisabled = false;
              if (minDate) {
                // Compare strings directly since they are YYYY-MM-DD
                isDisabled = dateString < minDate;
              }

              return (
                <button
                  key={day}
                  onClick={() => !isDisabled && handleDateSelect(day)}
                  disabled={isDisabled}
                  className={`
                    h-10 rounded-lg text-sm font-medium transition-all duration-200
                    ${
                      isDisabled
                        ? 'text-gray-300 cursor-not-allowed hover:bg-transparent'
                        : isSelected
                          ? 'bg-blue-600 text-white shadow-lg transform scale-105'
                          : isToday
                            ? 'bg-blue-100 text-blue-600 border border-blue-300'
                            : 'hover:bg-gray-100 text-gray-700'
                    }
                  `}
                >
                  {day}
                </button>
              );
            })}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={() => onDateChange(formatDateForInput(new Date()))}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Today
            </button>
          </div>
        </div>
      </>
    );
  };

  const handleRowClick = (booking: APIBooking) => {
    navigate(`/bookings/${booking.id}`);
  };

  const handleDeleteConfirm = async () => {
    if (!bookingToDelete) return;

    try {
      await realAPI.bookings.deleteBooking(bookingToDelete);
      if (bookings.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        await fetchBookings();
      }
      setIsDeleteModalOpen(false);
      setBookingToDelete(null);
      success('Booking Deleted', 'Booking deleted successfully');
    } catch (err) {
      console.error('Failed to delete booking:', err);
      error('Delete Failed', 'Failed to delete booking');
    }
  };
const getStatusColor = (status: string) => {
  const normalized = String(status).toLowerCase();

  switch (normalized) {
    case 'completed':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';

    case 'accepted':
      return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400';

    case 'pending':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';

    case 'pending_approval':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';

    case 'cancelled':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';

    case 'rejected':
      return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';

    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400';
  }
};

const parseAmount = (value?: number | string | null) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

const isRequestType = (booking?: APIBooking | null) => booking?.type === 'request';

const getDisplayServiceName = (booking?: APIBooking | null) => {
  if (!booking) return '-';

  if (isRequestType(booking)) {
    return booking.serviceName || booking.servicesInfo?.serviceName || '-';
  }

  return booking.servicesInfo?.serviceName || booking.serviceName || '-';
};

const getDisplayAmount = (booking?: APIBooking | null) => {
  if (!booking) return 0;

  if (isRequestType(booking)) {
    return parseAmount(booking.amount ?? booking.servicesInfo?.price ?? null);
  }

  return parseAmount(booking.servicesInfo?.price ?? booking.amount ?? null);
};

  // Table shimmer component
  const TableShimmer = () => (
    <div className="table-container">
      <div className="table-min-w">
        {/* Real Table Headers without sorting icons */}
        <div className="grid grid-cols-8 gap-3 p-4 pl-4 rounded-t-xl font-semibold text-gray-700 text-[15px] border-b border-gray-200 -ml-2 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm">
          <div className="text-gray-700 dark:text-white">Provider Name</div>
          <div className="text-gray-700 dark:text-white" style={{ paddingLeft: '5px' }}>
            Customer Name
          </div>
          <div className="text-gray-700 dark:text-white" style={{ paddingLeft: '10px' }}>
            Service
          </div>
          <div className="text-gray-700 dark:text-white" style={{ paddingLeft: '25px' }}>
            Price
          </div>
          <div className="text-gray-700 dark:text-white">Booking Date</div>
          <div className="text-gray-700 dark:text-white">Created At</div>
          <div
            className="text-gray-700 dark:text-white flex justify-start"
            style={{ paddingLeft: '15px' }}
          >
            Status
          </div>
          <div className="text-gray-700 dark:text-white text-center">Actions</div>
        </div>

        {/* Loader in table area */}
        <div className="flex flex-col items-center justify-center py-12">
          <div className="loader"></div>
        </div>
      </div>
    </div>
  );

  const selectedIsRequest = isRequestType(selectedBooking);
  const selectedServiceName = getDisplayServiceName(selectedBooking);
  const selectedAmount = getDisplayAmount(selectedBooking);
  const selectedServiceImage = selectedBooking
    ? selectedIsRequest
      ? getUserImageSrc(
          selectedBooking.providerInfo?.image,
          selectedBooking.providerInfo?.signInProvider
        )
      : typeof selectedBooking.servicesInfo?.serviceImage === 'string'
        ? selectedBooking.servicesInfo.serviceImage
        : null
    : null;

  return (
    <div className="overflow-hidden">
      <Card className="mb-2 p-[10px]">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 whitespace-nowrap">
            Bookings Management
          </h1>

          {/* Filters */}
          <div className="flex flex-wrap lg:items-center lg:justify-end gap-3 w-full">
            {/* Start Date Picker */}
            <div className="relative w-full sm:w-[180px] h-10 flex-shrink-0">
              <button
                onClick={() => setShowStartCalendar(true)}
                className="w-full h-full pl-10 pr-4 text-left bg-white border border-gray-300 rounded-lg 
            shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 
            transition-all duration-200 hover:border-gray-400 flex items-center"
              >
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <span className={filters.dateFrom ? 'text-gray-900' : 'text-gray-500'}>
                  {filters.dateFrom
                    ? new Date(filters.dateFrom).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : 'From Date'}
                </span>
              </button>

              <DatePicker
                selectedDate={filters.dateFrom}
                onDateChange={(date) => handleFilterChange('dateFrom', date)}
                onClose={() => setShowStartCalendar(false)}
                isOpen={showStartCalendar}
              />
            </div>

            {/* End Date Picker */}
            <div className="relative w-full sm:w-[180px] h-10 flex-shrink-0">
              <button
                onClick={() => setShowEndCalendar(true)}
                className="w-full h-full pl-10 pr-4 text-left bg-white border border-gray-300 rounded-lg 
            shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 
            transition-all duration-200 hover:border-gray-400 flex items-center"
              >
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <span className={filters.dateTo ? 'text-gray-900' : 'text-gray-500'}>
                  {filters.dateTo
                    ? new Date(filters.dateTo).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : 'To Date'}
                </span>
              </button>

              <DatePicker
                selectedDate={filters.dateTo}
                onDateChange={(date) => handleToDateChange(date)}
                onClose={() => setShowEndCalendar(false)}
                isOpen={showEndCalendar}
                minDate={filters.dateFrom}
              />
            </div>

            {/* Status Filter */}
            <div className="relative h-10 w-full sm:w-auto flex-shrink-0 transition-all duration-200">
              <Listbox value={filters.status || 'all'} onChange={handleStatusFilterChange}>
                {({ open }) => {
                  return (
                    <>
                      <Listbox.Button
                        className={`inline-flex items-center h-full pl-4 pr-10 text-left bg-white border border-gray-300
              rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500
              transition-all duration-200 hover:border-gray-400 cursor-pointer
              w-full sm:min-w-[140px] max-w-full sm:max-w-[400px]`}
                      >
                        <span className="block">
                          {statusOptions.find((o) => o.value === (filters.status || 'all'))
                            ?.label || 'Status'}
                        </span>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      </Listbox.Button>

                      <Listbox.Options
                        className="absolute z-20 mt-1 bg-white rounded-lg shadow-lg border border-gray-200
              min-w-[140px] max-w-[400px]"
                      >
                        {statusOptions.map((option) => (
                          <Listbox.Option
                            key={option.value}
                            value={option.value}
                            className={({ active }) =>
                              `px-4 py-2 cursor-pointer transition-colors ${active ? 'bg-gray-100' : ''}`
                            }
                          >
                            <span className={option.color}>{option.label}</span>
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </>
                  );
                }}
              </Listbox>
            </div>

            {/* Reset Filters */}
            <Button
              variant="secondary"
              onClick={handleResetFilters}
              className="px-4 h-10 w-full sm:w-[162px]"
            >
              Reset Filters
            </Button>
          </div>
        </div>
      </Card>

      {/* Bookings Grid Table */}
      <Card>
        {isLoading ? (
          <TableShimmer />
        ) : !bookings || bookings.length === 0 ? (
          <div className="table-container">
            <div className="table-min-w">
              {/* Grid Header */}
              <div className="grid grid-cols-8 gap-3 p-4 pl-4 rounded-t-xl font-semibold text-gray-700 text-[15px] border-b border-gray-200 -ml-2 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm">
                <div>
                  <div
                    className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                    onClick={() => handleSortClick('providerName')}
                  >
                    Provider Name{' '}
                    {sortField === 'providerName' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>
                </div>
                <div>
                  <div
                    className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                    style={{ paddingLeft: '5px' }}
                    onClick={() => handleSortClick('customerName')}
                  >
                    Customer Name{' '}
                    {sortField === 'customerName' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>
                </div>
                <div>
                  <div
                    className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                    style={{ paddingLeft: '10px' }}
                    onClick={() => handleSortClick('serviceName')}
                  >
                    Service {sortField === 'serviceName' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>
                </div>
                <div>
                  <div
                    className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                    style={{ paddingLeft: '25px' }}
                    onClick={() => handleSortClick('price')}
                  >
                    Price {sortField === 'price' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>
                </div>
                <div>
                  <div
                    className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                    onClick={() => handleSortClick('bookingDate')}
                  >
                    Booking Date {sortField === 'bookingDate' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>
                </div>
                <div>
                  <div
                    className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                    onClick={() => handleSortClick('createdAt')}
                  >
                    Created At {sortField === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>
                </div>
                <div className="flex justify-start">
                  <div
                    className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                    style={{ paddingLeft: '15px' }}
                    onClick={() => handleSortClick('status')}
                  >
                    Status {sortField === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>
                </div>
                <div className="text-center dark:text-white">Actions</div>
              </div>
              {/* Empty State inside table */}
              <div className="text-center py-12 bg-gray-50 rounded-b-xl">
                <img src="/NoRecord.svg" alt="No records found" className=" mx-auto mb-4 " />
              </div>
            </div>
          </div>
        ) : (
          <div className="table-container">
            <div className="table-min-w">
              {/* Grid Header */}
              <div className="grid grid-cols-8 gap-3 p-4 pl-4 rounded-t-xl font-semibold text-gray-700 text-[15px] border-b border-gray-200 -ml-2 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm">
                <div>
                  <div className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white">
                    Provider Name
                  </div>
                </div>
                <div>
                  <div
                    className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                    style={{ paddingLeft: '5px' }}
                  >
                    Customer Name
                  </div>
                </div>
                <div>
                  <div
                    className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                    style={{ paddingLeft: '10px' }}
                  >
                    Service
                  </div>
                </div>
                <div>
                  <div
                    className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                    style={{ paddingLeft: '25px' }}
                  >
                    Price
                  </div>
                </div>
                <div>
                  <div
                    className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                    onClick={() => handleSortClick('bookingDate')}
                  >
                    Booking Date {sortField === 'bookingDate' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>
                </div>
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  style={{ paddingLeft: '25px' }}
                  onClick={() => {
                    if (sortField === 'createdAt') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('createdAt');
                      setSortOrder('asc');
                    }
                    setPage(1);
                  }}
                >
                  Created At {sortField === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  style={{ paddingLeft: '25px' }}
                  onClick={() => {
                    if (sortField === 'status') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('status');
                      setSortOrder('asc');
                    }
                    setPage(1);
                  }}
                >
                  Status {sortField === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>
                <div className="text-center dark:text-white">Actions</div>
              </div>

              {/* Grid Body */}
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {bookings.map((booking) => (
                  <div
                    key={booking.id}
                    onClick={() => handleRowClick(booking)}
                    className="grid grid-cols-8 gap-4 p-4 hover:bg-blue-50 transition-colors items-center cursor-pointer dark:hover:bg-gray-700/50 p-[11px]"
                  >
                    <div className="truncate text-sm dark:text-white">
                      {booking.providerInfo?.name || '-'}
                    </div>
                    <div className="truncate text-sm dark:text-white">
                      {booking.customerInfo?.name || '-'}
                    </div>
                    <div className="truncate text-sm dark:text-white">
                      {getDisplayServiceName(booking)}
                    </div>
                    <div
                      className="font-semibold text-sm dark:text-white"
                      style={{ paddingLeft: '20px' }}
                    >
                      {formatCurrency(getDisplayAmount(booking))}
                    </div>
                    <div className="text-sm text-black-600 dark:text-gray-400">
                      {booking.bookingDate ? formatDate(booking.bookingDate) : '-'}
                    </div>
                    <div
                      className="text-sm text-black-600 dark:text-gray-400"
                      style={{ paddingLeft: '20px' }}
                    >
                      {booking.createdAt ? formatDate(booking.createdAt) : '-'}
                    </div>
                    <div className="flex items-center" style={{ paddingLeft: '20px' }}>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-md capitalize ${getStatusColor(
                          booking.status
                        )}`}
                      >
                        {booking.status === 'pending_approval'
                          ? 'Pending Approval'
                          : booking.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-center gap-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setBookingToDelete(booking.id);
                            setIsDeleteModalOpen(true);
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Booking"
                        >
                          <Trash2 className="w-6 h-6" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div className="flex flex-col sm:flex-row items-center justify-between p-4 border-t border-gray-200 gap-4 sm:gap-0">
                <div className="text-sm text-black-600 text-center sm:text-left font-medium dark:text-gray-400">
                  Showing {(page - 1) * LIMIT + 1} to {Math.min(page * LIMIT, total)} of {total}{' '}
                  bookings
                </div>
                <div className="flex gap-2">
                  <div className="flex items-center justify-between gap-4">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="px-4 py-2 text-sm font-medium text-gray-700">
                      Page {page} of {Math.ceil(total / LIMIT)}
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page * LIMIT >= total}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Booking Details Modal */}
      <Modal
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedBooking(null);
          navigate('/bookings');
        }}
        title="Booking Details"
        size="lg"
      >
        {selectedBooking && (
          <div className="space-y-4 sm:space-y-6">
            <div className="flex justify-end">
              <Button
                variant="secondary"
                onClick={() => navigate(`/chat?bookingId=${selectedBooking.id}`)}
              >
                View Chat
              </Button>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Service Info - Left Side (Full on mobile, 1/4 on desktop) */}
              <div className="col-span-1 flex">
                <div className="w-full h-auto lg:h-[85%]">
                  <div className="bg-gray-50 rounded-lg p-3 lg:p-4 space-y-4 h-full">
                    {/* Move header INSIDE the box */}
                    <h3 className="text-lg font-semibold text-gray-900">Service Information</h3>

                    {/* Service Image (if available) */}
                    {selectedServiceImage ? (
                      <img
                        src={selectedServiceImage}
                        alt="Service"
                        className="w-14 h-14 rounded object-cover"
                        style={{ marginTop: '-10px' }}
                      />
                    ) : null}

                    {/* Service Details */}
                    <div>
                      <p className="text-gray-900 font-medium">
                        {selectedServiceName}
                      </p>
                      <p className="text-sm text-gray-600">
                        Category: {selectedBooking.categoryInfo?.name || '-'}
                      </p>
                      <p className="text-sm text-gray-600">
                        Price: {formatCurrency(selectedAmount)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Description:{' '}
                        {selectedIsRequest
                          ? selectedBooking.description || selectedBooking.servicesInfo?.description || '-'
                          : selectedBooking.servicesInfo?.description || '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Booking & Details - Right Side (Full on mobile, 3/4 on desktop) */}
              <div className="col-span-1 lg:col-span-3 flex">
                <div className="w-full h-full bg-gray-50 rounded-lg p-4 lg:p-5 space-y-6">
                  {/* Booking Info */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      Booking Information
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Booking ID</label>
                        <p className="text-gray-900 font-mono">{selectedBooking.id}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Booking Date</label>
                        <p className="text-gray-900">{formatDate(selectedBooking.bookingDate)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Created At</label>
                        <p className="text-gray-900">{formatDate(selectedBooking.createdAt)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Status</label>
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                            selectedBooking.status
                          )}`}
                        >
                          {selectedBooking.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Provider & Customer Info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Customer Info */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        Customer Information
                      </h3>
                      <div className="space-y-1">
                        <div
                          className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/customers/${selectedBooking.customerInfo?.id}`);
                          }}
                        >
                          {getUserImageSrc(
                            selectedBooking.customerInfo?.image,
                            selectedBooking.customerInfo?.signInProvider
                          ) ? (
                            <img
                              src={
                                getUserImageSrc(
                                  selectedBooking.customerInfo?.image,
                                  selectedBooking.customerInfo?.signInProvider
                                )!
                              }
                              className="w-10 h-10 rounded-full object-cover"
                              alt={selectedBooking.customerInfo.name}
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                              {selectedBooking.customerInfo?.name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                          )}
                          <div>
                            <p className="text-gray-900 font-medium hover:text-blue-600 transition-colors">
                              {selectedBooking.customerInfo?.name || '-'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Provider Info */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        Provider Information
                      </h3>
                      <div className="space-y-1">
                        <div
                          className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/providers/${selectedBooking.providerInfo?.id}`);
                          }}
                        >
                          {getUserImageSrc(
                            selectedBooking.providerInfo?.image,
                            selectedBooking.providerInfo?.signInProvider
                          ) ? (
                            <img
                              src={
                                getUserImageSrc(
                                  selectedBooking.providerInfo?.image,
                                  selectedBooking.providerInfo?.signInProvider
                                )!
                              }
                              className="w-10 h-10 rounded-full object-cover"
                              alt={selectedBooking.providerInfo.name}
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white font-semibold">
                              {selectedBooking.providerInfo?.name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                          )}
                          <div>
                            <p className="text-gray-900 font-medium hover:text-blue-600 transition-colors">
                              {selectedBooking.providerInfo?.name || '-'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Address Info */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      Address Information
                    </h3>
                    <p className="text-gray-900">
                      {selectedBooking.bookingAddressInfo || 'No address provided'}
                    </p>
                  </div>

                  {/* Booking Rating Info */}
                  {selectedBooking.ratingInfo && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Customer Rating</h3>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <svg
                              key={i}
                              className={`w-5 h-5 ${i < (selectedBooking.ratingInfo?.rating || 0) ? 'text-yellow-400' : 'text-gray-300'}`}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                        </div>
                        <span className="text-lg font-semibold text-gray-900">
                          {selectedBooking.ratingInfo?.rating || 0}/5
                        </span>
                      </div>
                      {selectedBooking.ratingInfo?.review && (
                        <div>
                          <label className="text-sm font-medium text-gray-600">Review</label>
                          <p className="text-gray-700 mt-1">{selectedBooking.ratingInfo?.review}</p>
                        </div>
                      )}
                      <p className="text-sm text-gray-500 mt-2">
                        Rated on:{' '}
                        {selectedBooking.ratingInfo?.createdAt
                          ? formatDate(selectedBooking.ratingInfo.createdAt)
                          : selectedBooking.createdAt
                            ? formatDate(selectedBooking.createdAt)
                            : 'N/A'}
                      </p>
                    </div>
                  )}

                  {/* No Rating Message for completed bookings */}
                  {selectedBooking.status === 'completed' && !selectedBooking.ratingInfo && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Customer Rating</h3>
                      <p className="text-sm text-gray-500 italic">No rating provided by customer</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Booking"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete this booking? This action cannot be undone.
          </p>
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
